import type { UploadCompleteResponse, UploadSession, VideoCreate } from '@streamr/shared';
import { api, request } from '../api/client';
import { routes } from '../api/routes';
import { ApiError } from '../api/errors';
import { fileFingerprint, planParts, progressRatio, remainingParts, type PartPlan } from './chunker';

/**
 * Drives one resumable upload: init, parts, complete.
 *
 * Parts are PUT through the API rather than straight to object storage. That
 * follows from foundation contract 4/5 -- the MinIO bucket is private in every
 * environment, with no public policy ever -- so the browser has no credential
 * to write with and no route to the bucket. It costs a proxy hop and buys a
 * bucket that is never reachable from a browser.
 */

export type UploadPhase =
  | 'idle'
  | 'initializing'
  | 'uploading'
  | 'paused'
  | 'finalizing'
  | 'processing'
  | 'done'
  | 'error';

export interface UploadProgress {
  phase: UploadPhase;
  /** 0..1 across the whole file. */
  ratio: number;
  uploadedParts: number;
  totalParts: number;
  uploadId: string | null;
  videoId: string | null;
  error: string | null;
}

const RESUME_STORAGE_KEY = 'streamr.upload.resume';

interface ResumeRecord {
  fingerprint: string;
  uploadId: string;
  videoId: string;
  totalBytes: number;
}

const readResume = (): ResumeRecord | null => {
  try {
    const raw = window.localStorage.getItem(RESUME_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<ResumeRecord>;
    if (
      typeof parsed.fingerprint === 'string' &&
      typeof parsed.uploadId === 'string' &&
      typeof parsed.videoId === 'string' &&
      typeof parsed.totalBytes === 'number'
    ) {
      return parsed as ResumeRecord;
    }
    return null;
  } catch {
    return null;
  }
};

const writeResume = (record: ResumeRecord | null): void => {
  try {
    if (record === null) window.localStorage.removeItem(RESUME_STORAGE_KEY);
    else window.localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // A viewer with storage disabled simply cannot resume across a reload.
  }
};

/**
 * True when a stored upload can be resumed with this file. The byte length must
 * match what the server recorded: the fingerprint alone (name+size+mtime) is a
 * heuristic, and resuming the wrong file would splice two sources into one
 * object rather than fail.
 */
export const canResume = (record: ResumeRecord | null, file: File, serverTotalBytes: number): boolean =>
  record !== null &&
  record.fingerprint === fileFingerprint(file) &&
  record.totalBytes === file.size &&
  serverTotalBytes === file.size;

export interface UploadControllerOptions {
  onProgress: (progress: UploadProgress) => void;
}

export class UploadController {
  private aborter: AbortController | null = null;
  private paused = false;
  private plan: PartPlan[] = [];
  private received = new Set<number>();
  private session: UploadSession | null = null;
  private phase: UploadPhase = 'idle';
  private error: string | null = null;

  private readonly onProgress: (progress: UploadProgress) => void;

  constructor(options: UploadControllerOptions) {
    this.onProgress = options.onProgress;
  }

  private emit(): void {
    this.onProgress({
      phase: this.phase,
      ratio: progressRatio(this.plan, [...this.received]),
      uploadedParts: this.received.size,
      totalParts: this.plan.length,
      uploadId: this.session?.uploadId ?? null,
      videoId: this.session?.videoId ?? null,
      error: this.error,
    });
  }

  private setPhase(phase: UploadPhase, error: string | null = null): void {
    this.phase = phase;
    this.error = error;
    this.emit();
  }

  /**
   * Starts, or picks up where a previous attempt stopped. The server's part
   * list is the source of truth for what has landed -- never the client's
   * memory of what it sent.
   */
  async start(file: File, metadata: VideoCreate): Promise<string | null> {
    this.paused = false;
    this.error = null;
    this.aborter = new AbortController();

    try {
      const resumed = await this.resumeOrInit(file, metadata);
      this.session = resumed;
      this.plan = planParts(file.size, resumed.partSizeBytes);
      this.received = new Set(resumed.parts.map((part) => part.partNumber));
      writeResume({
        fingerprint: fileFingerprint(file),
        uploadId: resumed.uploadId,
        videoId: resumed.videoId,
        totalBytes: file.size,
      });

      this.setPhase('uploading');
      await this.sendParts(file);

      if (this.paused) {
        this.setPhase('paused');
        return null;
      }

      this.setPhase('finalizing');
      const completed = await this.complete();
      writeResume(null);
      this.setPhase('processing');
      return completed.videoId;
    } catch (cause) {
      if (this.paused) {
        this.setPhase('paused');
        return null;
      }
      this.setPhase('error', cause instanceof Error ? cause.message : 'Upload failed');
      return null;
    }
  }

  /**
   * Stops after the part in flight. The upload row and everything already
   * stored survive; `start()` with the same file resumes from the server's part
   * list.
   */
  pause(): void {
    this.paused = true;
    this.aborter?.abort();
  }

  private async resumeOrInit(file: File, metadata: VideoCreate): Promise<UploadSession> {
    const record = readResume();
    if (record !== null && record.fingerprint === fileFingerprint(file)) {
      try {
        const existing = await api.get<UploadSession>(routes.uploads.detail(record.uploadId));
        if (existing.status !== 'ABORTED' && canResume(record, file, existing.totalBytes)) {
          return existing;
        }
      } catch (cause) {
        // A 404 means the server forgot the upload; fall through and start over
        // rather than stranding the user on a resume that cannot work.
        if (!(cause instanceof ApiError) || !cause.isNotFound) throw cause;
      }
      writeResume(null);
    }

    this.setPhase('initializing');
    return api.post<UploadSession>(routes.uploads.create(), {
      filename: file.name,
      contentType: file.type !== '' ? file.type : 'application/octet-stream',
      totalBytes: file.size,
      video: metadata,
    });
  }

  private async sendParts(file: File): Promise<void> {
    const session = this.session;
    if (session === null) throw new Error('upload not initialized');

    /*
     * Parts go up one at a time, in order. Parallelism would be faster but a
     * browser uploading 8MB chunks over a domestic connection is already
     * saturating it, and serial parts keep the server's received-parts list a
     * meaningful resume point rather than a set with holes in it.
     */
    for (const part of remainingParts(this.plan, [...this.received])) {
      if (this.paused) return;
      const blob = file.slice(part.start, part.end);
      await request<unknown>(routes.uploads.part(session.uploadId, part.partNumber), {
        method: 'PUT',
        rawBody: blob,
        headers: { 'Content-Type': 'application/octet-stream' },
        signal: this.aborter?.signal,
      });
      this.received.add(part.partNumber);
      this.emit();
    }
  }

  private async complete(): Promise<UploadCompleteResponse> {
    const session = this.session;
    if (session === null) throw new Error('upload not initialized');
    return api.post<UploadCompleteResponse>(routes.uploads.complete(session.uploadId));
  }
}
